import * as Sentry from '@sentry/react';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import {
  type ActionFunction,
  createBrowserRouter,
  type LoaderFunction,
} from 'react-router';
import { RouterProvider } from 'react-router/dom';

import Nh1816Favicon from '@/assets/images/nh-favicon.ico';
import ErrorBoundary from '@/components/common/error-boundary';
import HydrateFallback from '@/components/common/hydrate-fallback';
import { LoginGuard } from '@/components/common/login-guard';
import { Nh1816Layout } from '@/components/layouts/nh1816-layout';
import { paths } from '@/config/paths';
import { setBrowserTabSettings } from '@/lib/browser-tab';

interface RouteModule {
  clientLoader?: (queryClient: QueryClient) => LoaderFunction;
  clientAction?: (queryClient: QueryClient) => ActionFunction;
  default?: React.ComponentType;
  [key: string]: unknown;
}

const convert =
  (queryClient: QueryClient) =>
  (m: RouteModule): Record<string, unknown> => {
    const { clientLoader, clientAction, default: Component, ...rest } = m;
    return {
      ...rest,
      loader: clientLoader?.(queryClient),
      action: clientAction?.(queryClient),
      Component,
    };
  };

const commonProps = {
  ErrorBoundary: ErrorBoundary,
  HydrateFallback: HydrateFallback,
};

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);

const createAppRouter = (
  queryClient: QueryClient,
): ReturnType<typeof createBrowserRouter> =>
  sentryCreateBrowserRouter([
    {
      path: paths.root.path,
      element: <Nh1816Layout />,
      loader: async (): Promise<void> => {
        setBrowserTabSettings('Afschrijvingstool - Nh1816', Nh1816Favicon);
      },
      ...commonProps,
      children: [
        {
          path: `/:clientId?`,
          lazy: () =>
            import('./routes/nh1816/claim-selection').then(
              convert(queryClient),
            ),
        },
        {
          path: `/:clientId/:reference`,
          lazy: () =>
            import('./routes/nh1816/insurance-tool').then(convert(queryClient)),
        },
      ],
    },
    {
      path: paths.auth.path,
      ...commonProps,
      element: <LoginGuard />,
      children: [
        {
          path: paths.auth.login.path,
          lazy: () => import('./routes/auth/login').then(convert(queryClient)),
        },
        {
          path: paths.auth.callback.path,
          lazy: () =>
            import('./routes/auth/callback').then(convert(queryClient)),
        },
      ],
    },
    {
      path: '*',
      ...commonProps,
      lazy: () => import('./routes/error').then(convert(queryClient)),
    },
  ]);

export const AppRouter = (): JSX.Element => {
  const queryClient = useQueryClient();

  const router = createAppRouter(queryClient);

  return <RouterProvider router={router} />;
};
